/**
 * Registers /sw.js (production builds only) so vs-computer play works offline
 * after the first visit. A new deploy installs a new worker that takes over
 * right away; the page itself reloads only at a safe moment (the home screen),
 * never in the middle of a game.
 */
import { useEffect, useRef } from 'react';
import type { Route } from './router';

let updateReady = false;
const listeners = new Set<() => void>();

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  // Only an update counts: the first install also fires controllerchange (clients.claim).
  let hadController = !!sw.controller;
  sw.addEventListener('controllerchange', () => {
    if (hadController) {
      updateReady = true;
      listeners.forEach((fn) => fn());
    }
    hadController = true;
  });
  const register = () => {
    sw.register('/sw.js').then(
      (reg) => {
        // Long-lived home-screen apps: look for a new build whenever the app comes back.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') reg.update().catch(() => {});
        });
      },
      () => {},
    );
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/**
 * Apply an update at a safe moment: on the home screen the page reloads at
 * once (a solo game is saved after every move); elsewhere we say so once and
 * wait until the player is back home.
 */
export function useServiceWorkerUpdates(route: Route, notify: (text: string) => void): void {
  const told = useRef(false);
  const routeRef = useRef(route);
  routeRef.current = route;
  useEffect(() => {
    const check = () => {
      if (!updateReady) return;
      if (routeRef.current.name === 'home') {
        location.reload();
      } else if (!told.current) {
        told.current = true;
        notify('Ancient Nine was updated. The new version loads when you’re back on the home screen.');
      }
    };
    listeners.add(check);
    check();
    return () => {
      listeners.delete(check);
    };
  }, [route, notify]);
}
